import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { ErrorBox, dash } from '../components/DataTable';

/**
 * 접수 복사.
 *
 * 반복 운행(주로 통근)을 계약기간 일자전개 대신 **월별 복사**로 표현한다.
 * 레거시는 계약기간을 하루씩 돌아 스케줄 1건이 수백~수만 행을 만들었다.
 *
 * ⚠️ **배정(차량·기사)은 복사되지 않는다.** 새 날짜에 그 차와 기사가 실제로 가능한지는
 *    배정할 때 검사한다. 배정을 같이 복사하면 검사를 건너뛴 유령 배정이 생긴다.
 *    화면에 그 사실을 적어 둔다 — 안 적으면 "차가 안 붙어 있다"는 문의가 온다.
 */
export default function RecvCopy({
  recv,
  onClose,
  onCopied,
  preset,
}: {
  recv: { recv_id: number; cust_name?: unknown; recv_type?: unknown };
  onClose: () => void;
  onCopied: (msg: string) => void;
  /**
   * `Ctrl+V` 로 열 때 미리 채워 둘 것(2026-08-02).
   * ⚠️ **자동으로 저장하지는 않는다.** 붙여넣기는 노선·배차를 새로 만드는 일이고,
   *    배차는 시간중복 가드에 걸려 건너뛸 수 있다 — 무엇이 어디로 가는지 보고 누르게 한다.
   */
  preset?: { disptIds?: number[]; dest?: 'new' | 'same' | 'other'; otherRecvId?: number };
}) {
  const recvId = Number(recv.recv_id);
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [months, setMonths] = useState(1);
  const [days, setDays] = useState(0);
  /** 어디에 붙일지. 'new' = 접수째 새로 · 'same' = 이 접수에 한 줄 더 · 'other' = 다른 접수로. */
  const [dest, setDest] = useState<'new' | 'same' | 'other'>(preset?.dest ?? 'new');
  const [otherRecvId, setOtherRecvId] = useState(preset?.otherRecvId ? String(preset.otherRecvId) : '');
  /** 배차(차량·기사 배정)까지 복사. 기본은 **끔** — 그 날짜에 그 차가 되는지 모른다. */
  const [withAssignments, setWithAssignments] = useState(false);
  const [skipped, setSkipped] = useState<
    { carNo: string | null; driverName: string | null; reason: string }[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .listRecvDispts({ recvId })
      .then((r) => {
        if (cancelled) return;
        setRows(r.rows);
        // 기본은 **전체 선택**. `Ctrl+C` 로 노선을 골라 담았으면 그것만 켠다(부분 복사).
        // ⚠️ 담아 둔 노선이 그사이 사라졌을 수 있다 — 지금 목록에 있는 것만 남긴다.
        const here = new Set(r.rows.map((x) => Number(x.dispt_id)));
        const want = preset?.disptIds?.filter((id) => here.has(id)) ?? [];
        setPicked(want.length > 0 ? new Set(want) : here);
      })
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : String(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recvId]);

  const all = picked.size === rows.length && rows.length > 0;

  function toggle(id: number) {
    setPicked((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function copy() {
    if (picked.size === 0) {
      setError('복사할 운행을 하나 이상 고르세요.');
      return;
    }
    const target =
      dest === 'same' ? recvId : dest === 'other' ? Number(otherRecvId.trim()) : undefined;
    if (dest === 'other' && (!Number.isInteger(target) || (target as number) <= 0)) {
      setError('붙여 넣을 접수번호를 입력하세요.');
      return;
    }

    setBusy(true);
    setError(null);
    setSkipped([]);
    try {
      const r = await api.copyRecv({
        recvId,
        disptIds: all ? undefined : [...picked],
        shiftMonths: months,
        shiftDays: days,
        targetRecvId: target,
        withAssignments: withAssignments || undefined,
      });

      // 건너뛴 배차가 있으면 **닫지 않고 그 자리에 보여준다.** 알림 한 줄로 흘리면
      // 담당자는 몇 대가 왜 안 붙었는지 모른 채 배차완료로 착각한다.
      if (r.skipped?.length) {
        setSkipped(r.skipped);
        setBusy(false);
        return;
      }

      const where = r.createdRecv ? `접수 ${r.recvId}번으로` : `접수 ${r.recvId}번에`;
      const asg = withAssignments
        ? ` 배차 ${r.assignedCopied}대도 함께 붙였습니다.`
        : ' 차량은 새로 배정하세요.';
      onCopied(`${where} 노선 ${r.disptCount}건을 복사했습니다.${asg}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <div className="card space-y-4 p-5">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-base font-bold">
          접수 복사 <span className="font-normal text-ink-soft">{dash(recv.cust_name)} · {recvId}번</span>
        </h2>
        <button className="btn-ghost ml-auto" onClick={onClose} disabled={busy}>
          취소
        </button>
        <button className="btn-primary" onClick={copy} disabled={busy || loading}>
          {busy ? '복사 중…' : `${picked.size}건 복사`}
        </button>
      </div>

      {error && <ErrorBox message={error} />}

      <div className="flex flex-wrap items-end gap-4">
        <label className="block">
          <span className="mb-1 block text-sm font-medium">
            월 이동 <span className="ml-1 text-xs font-normal text-ink-faint">+1 = 다음 달 같은 날짜</span>
          </span>
          <input
            type="number"
            className="field w-28"
            value={months}
            onChange={(e) => setMonths(Number(e.target.value))}
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-sm font-medium">
            일 이동 <span className="ml-1 text-xs font-normal text-ink-faint">요일 맞추려면 7의 배수</span>
          </span>
          <input type="number" className="field w-28" value={days} onChange={(e) => setDays(Number(e.target.value))} />
        </label>
        <p className="text-sm text-ink-soft">
          출발·도착 시각과 <b>정산월</b>이 함께 이동합니다. 월 이동 시 말일은 자동으로 맞춰집니다(1/31 → 2/28).
        </p>
      </div>

      {/* 어디에 붙일지 — 접수째 새로 만드는 것과 기존 접수에 노선을 늘리는 것은 다른 일이다. */}
      <div className="space-y-2">
        <span className="block text-sm font-medium">붙여 넣을 곳</span>
        <div className="flex flex-wrap items-center gap-4 text-sm">
          {(
            [
              ['new', '새 접수를 만든다'],
              ['same', '이 접수에 노선을 더한다'],
              ['other', '다른 접수에 붙인다'],
            ] as const
          ).map(([id, label]) => (
            <label key={id} className="flex cursor-pointer items-center gap-1.5">
              <input type="radio" checked={dest === id} onChange={() => setDest(id)} />
              {label}
            </label>
          ))}
          {dest === 'other' && (
            <input
              className="field w-32"
              placeholder="접수번호"
              value={otherRecvId}
              onChange={(e) => setOtherRecvId(e.target.value)}
            />
          )}
        </div>
        {dest !== 'new' && (
          <p className="break-keep text-xs text-ink-soft">
            접수 헤더(거래처·청구방법·거래처담당자·버스인접수담당자)는 <b>붙여 넣는 접수의 것을 그대로 둡니다</b> — 노선만 더합니다.
          </p>
        )}
      </div>

      {/* 배차복사 — 켤 수 있게 하되 무엇이 일어나는지 그 자리에 적는다. */}
      <div className="space-y-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
        <label className="flex cursor-pointer items-center gap-2 font-medium">
          <input
            type="checkbox"
            checked={withAssignments}
            onChange={(e) => setWithAssignments(e.target.checked)}
          />
          차량·기사 배정도 함께 복사
        </label>
        <p className="break-keep">
          {withAssignments ? (
            <>
              옮긴 날짜에 그 차·기사가 <b>이미 다른 운행에 잡혀 있으면 그 건만 건너뜁니다</b> —
              나머지는 그대로 붙고, 건너뛴 건은 복사 후 목록으로 보여 드립니다.
            </>
          ) : (
            <>배정은 복사되지 않습니다. 복사 후 새로 배정하세요 — 그래야 시간 중복 검사가 걸립니다.</>
          )}
        </p>
      </div>

      {skipped.length > 0 && (
        <div className="space-y-2 rounded-lg border border-amber-400 bg-amber-50 px-3 py-2 text-sm">
          <p className="font-semibold text-amber-900">
            노선은 복사됐지만 배차 {skipped.length}대는 붙이지 못했습니다 — 그 시간에 이미 잡혀 있습니다.
          </p>
          <ul className="space-y-0.5 text-xs text-amber-900">
            {skipped.map((s, i) => (
              <li key={i}>
                · {dash(s.carNo)} / {dash(s.driverName)} — {s.reason}
              </li>
            ))}
          </ul>
          <p className="text-xs text-ink-soft">복사는 끝났습니다. 이 대수는 배차 화면에서 다른 차로 채우세요.</p>
          <button className="btn-ghost py-1 text-xs" onClick={onClose}>
            닫기
          </button>
        </div>
      )}

      <div>
        <div className="mb-2 flex items-center gap-3 text-sm">
          <span className="font-semibold text-ink-soft">복사할 운행 {loading ? '' : `${picked.size}/${rows.length}`}</span>
          {!loading && rows.length > 0 && (
            <button
              className="btn-ghost py-1 text-xs"
              onClick={() => setPicked(all ? new Set() : new Set(rows.map((x) => Number(x.dispt_id))))}
            >
              {all ? '전체 해제' : '전체 선택'}
            </button>
          )}
        </div>

        {loading ? (
          <p className="text-sm text-ink-soft">불러오는 중…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-ink-faint">이 접수에는 운행이 없습니다.</p>
        ) : (
          <ul className="divide-y divide-line rounded-lg border border-line">
            {rows.map((x) => {
              const id = Number(x.dispt_id);
              return (
                <li key={id}>
                  <label className="flex cursor-pointer items-center gap-3 px-3 py-2 text-sm hover:bg-canvas">
                    <input type="checkbox" checked={picked.has(id)} onChange={() => toggle(id)} />
                    <span className="w-36 shrink-0 tabular-nums text-ink-soft">{dash(x.start_time)}</span>
                    <span className="min-w-0 flex-1 truncate">
                      {dash(x.dispt_name)}
                      <span className="ml-2 text-ink-faint">
                        {dash(x.start_loc)} → {dash(x.end_loc)}
                      </span>
                    </span>
                    <span className="shrink-0 text-ink-faint">
                      {dash(x.car_kind)} · {dash(x.car_cnt)}대 · {dash(x.trip_type)}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
